import {
  Button,
  Checkbox,
  DropdownMenu,
  type DropdownMenuItem,
  Icon,
  Tooltip,
} from '@timelyapp/react';
import classNames from 'classnames';
import { BorderlessStar } from 'components/Icons';
import Popover from 'components/popovers/Popover';
import React, { Component, useState } from 'react';
import type { ModalMessageKeyTypes } from 'redux/modules/subscriptions.types';
import styles from './ActionSection.module.scss';
import { Section } from './Section';

interface Props {
  showTimestamp: boolean;
  isEstimatedDurationUsed: boolean;
  date: string;
  onToggleTimer: () => void;
  onToggleTimestamp: () => void;
  onToggleHourHistoryModal: () => void;
  onToggleEstimatedDuration: () => void;
  onShowCopyModal: () => void;
  onShowMoveModal: () => void;
  onToggleSubscriptionNoticeModal: (messageKey: ModalMessageKeyTypes) => unknown;
  onToggleBillable: () => void;
  onCloseBillableChangedWarning: (dontShowAgain: boolean) => void;
  disabled: boolean;
  standalone?: boolean;
  isSaveDisabled: boolean; // todo: really though, ui should be passed directly,,
  isBasedOnSuggestion: boolean;
  isCopyDisabled: boolean;
  isAddPlannedTimeDisbled: boolean;
  isMoveDisabled: boolean;
  isActingAsOtherUser?: boolean;
  isAuditFeatureEnabled: boolean;
  isBillable: boolean;
  isProjectBillable: boolean;
  isNewDraft: boolean;
  style?: React.CSSProperties;
  showBillableChangedWarning?: boolean;
  hourStateButtonElement: React.ReactNode;
  isTimerDisabled: boolean;
  isTimerHidden: boolean;
  isTimerRunning: boolean;
}

type ClickableItem = Partial<DropdownMenuItem> & {
  hideWhenActive?: boolean;
  hidden?: boolean;
  hasExtraEmphasis?: boolean;
  onClick: () => void;
  isModified?: boolean;
  wrapper?: React.FC<{ target: React.ReactElement }>;
  active?: boolean;
  label: string;
  icon?: React.ReactNode;
  isLockedFeature?: boolean;
  tooltip?: string;
  disabled?: boolean;
  element?: React.ReactNode;
};

export class ActionSection extends Component<Props> {
  static defaultProps: Partial<Props> = {
    disabled: false,
  };

  renderButtons = (buttons: Array<ClickableItem>): Array<React.ReactNode> => {
    // Certain buttons should be hidden when active
    const actualButtons = buttons.filter(button => {
      return !button.active || !button.hideWhenActive || button.hidden;
    });

    return actualButtons.map((button, idx) => this.renderSingleButton(button, idx));
  };

  /**
   * @warning This isn't directly related, should instead be solved via already open PR
   * @link https://github.com/Timely/Frontend/pull/5012#issuecomment-2536964394
   */
  renderSingleButton = (button: ClickableItem, index: number) => {
    const key = button.label || index;

    if ('element' in button) {
      return <React.Fragment key={key}>{button.element}</React.Fragment>;
    }

    const element = (
      <React.Fragment key={key}>
        <Button
          data-testid={`${button.label || 'unknown'}_action_section`}
          size="xsmall"
          priority="xlow"
          disabled={button.disabled || this.props.disabled}
          onClick={() => button.onClick()}
        >
          {button.icon}
          {button.label}
        </Button>
        {button.isLockedFeature ? <BorderlessStar className={styles.lockedFeatureIcon} /> : null}
      </React.Fragment>
    );
    if (button.wrapper) {
      return (
        <React.Fragment key={key}>
          <button.wrapper target={element} />
        </React.Fragment>
      );
    }
    if (button.tooltip) {
      return (
        <React.Fragment key={key}>
          <Tooltip tooltip={button.tooltip}>{element}</Tooltip>
        </React.Fragment>
      );
    }
    return <React.Fragment key={key}>{element}</React.Fragment>;
  };

  renderMoreMenu = (menuItems: Array<DropdownMenuItem>) => {
    if (!menuItems.length) {
      return null;
    }

    return (
      <DropdownMenu
        closeOnSelect
        trigger={
          <Button
            priority="xlow"
            size="small"
            className={styles.moreButton}
            data-testid="more_action_section"
          >
            <Icon name="DotsHorizontal" type="solid" />
          </Button>
        }
        items={menuItems}
        align="end"
        // TODO: find a more suitable value
        style={{ zIndex: 1002 }}
        contentClassName="ignore-react-onclickoutside"
      />
    );
  };

  getMoreMenuItems = (): Array<DropdownMenuItem> => {
    const {
      isCopyDisabled,
      isMoveDisabled,
      isBasedOnSuggestion,
      isNewDraft,
      isEstimatedDurationUsed,
      isAddPlannedTimeDisbled,
    } = this.props;

    return [
      {
        type: 'item',
        onSelect: this.props.onShowCopyModal,
        label: 'Copy to another date',
        icon: <Icon name="Copy01" type="solid" />,
        disabled: isCopyDisabled,
        hidden: isBasedOnSuggestion || isNewDraft,
      },
      {
        type: 'item',
        onSelect: this.props.onShowMoveModal,
        label: 'Move to another date',
        icon: <Icon name="ArrowRight" type="solid" />,
        disabled: !isNewDraft && isMoveDisabled,
        hidden: isNewDraft || isBasedOnSuggestion,
      },
      {
        type: 'item',
        onSelect: this.props.onToggleHourHistoryModal,
        label: 'Show history',
        icon: <Icon name="FlipBackward" type="solid" />,
        onLockedFeatureClick: () =>
          this.props.onToggleSubscriptionNoticeModal('featureAuditRequiresUpgrade'),
        isLockedFeature: !this.props.isAuditFeatureEnabled,
        hidden: isNewDraft,
      },
      {
        type: 'item',
        onSelect: this.props.onToggleEstimatedDuration,
        label: isEstimatedDurationUsed ? 'Clear planned time' : 'Add planned time',
        icon: <Icon name="CalendarPlus02" type="solid" />,
        disabled: isAddPlannedTimeDisbled,
      },
    ].filter(item => !item.hidden) as Array<DropdownMenuItem>;
  };

  getButtonsList = (): Array<ClickableItem> => {
    const { showTimestamp, isSaveDisabled, hourStateButtonElement } = this.props;
    return [
      {
        onClick: this.props.onToggleTimestamp,
        tooltip: showTimestamp ? 'Clear from & to' : 'Set from & to',
        label: 'From & To',
        active: showTimestamp,
        hideWhenActive: true,
        icon: <Icon name="Clock" type="solid" />,
      },
      {
        onClick: this.props.onToggleBillable,
        label: this.props.isBillable ? 'Billable' : 'Non-billable',
        disabled: isSaveDisabled || this.props.showBillableChangedWarning,
        tooltip: this.props.isBillable ? 'Mark entry as non-billable' : 'Mark entry as billable',
        icon: <Icon name="CurrencyDollarCircle" type="solid" />,
        hidden: !this.props.isProjectBillable,
        isModified: this.props.isBillable !== this.props.isProjectBillable,
        wrapper: this.props.showBillableChangedWarning
          ? ({ target }: any) => {
              const [isOpen, setIsOpen] = useState(true);
              const [dontShowAgain, setDontShowAgain] = useState(false);
              const handleClose = () => {
                setIsOpen(false);
                this.props.onCloseBillableChangedWarning?.(dontShowAgain);
              };
              return (
                <Popover
                  isOpen={isOpen}
                  ignoreTargetOnClick={false}
                  target={target}
                  onClose={handleClose}
                  stopPropagation={false}
                >
                  <div className={styles.billablePrompt}>
                    <p>Billable state was updated to match the new project.</p>
                    <p>
                      <Checkbox
                        label="Don't show again"
                        checked={dontShowAgain}
                        onChangeChecked={setDontShowAgain}
                        onClick={e => {
                          e.stopPropagation();
                        }}
                      />
                    </p>
                    <p>
                      <Button priority="low" size="small" onClick={handleClose}>
                        Close
                      </Button>
                    </p>
                  </div>
                </Popover>
              );
            }
          : undefined,
      },
      {
        element: hourStateButtonElement,
      },
    ].filter(
      button => !button.hidden && !(button?.hideWhenActive && button?.active)
    ) as Array<ClickableItem>;
  };

  render() {
    const { standalone, style } = this.props;
    const buttons: Array<ClickableItem> = this.getButtonsList();
    const moreMenuItems: Array<DropdownMenuItem> = this.getMoreMenuItems();
    const noBorder = this.props.isActingAsOtherUser || standalone;

    return (
      <Section
        noBorder={!noBorder}
        noBottom={!noBorder}
        className={classNames(styles.container, { [styles.standaloneContainer]: standalone })}
        style={style}
      >
        {this.renderButtons(buttons)}
        {moreMenuItems.length > 0 && this.renderMoreMenu(moreMenuItems)}
      </Section>
    );
  }
}

export default React.memo(ActionSection);
